/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateConversationSharePathParameters,
  DeleteConversationPathParameters,
  ExportConversationToSkillPathParameters,
  ExportConversationToWorkflowPathParameters,
  ForkConversationPathParameters,
  GetConversationFilesPathParameters,
  GetConversationParams,
  GetConversationPathParameters,
  GetConversationSharePathParameters,
  GetFilePreviewParams,
  GetFilePreviewPathParameters,
  GetMessageToolExecutionsPathParameters,
  GetProgramPermissionsByProgramIdPathParameters,
  GetSharedProgramsParams,
  GetToolExecutionPathParameters,
  GetUserConversationsParams,
  GetUserProgramsParams,
  GrantPermissionPathParameters,
  RenameConversationPathParameters,
  RevokeConversationSharePathParameters,
  SearchConversationFilePreviewParams,
  SearchConversationFilePreviewPathParameters,
  StreamExportConversationToSkillPathParameters,
  StreamExportConversationToWorkflowPathParameters,
  SummarizeConversationPathParameters,
  TLChatResponse,
  TLConversationFile,
  TLConversationForkRequest,
  TLConversationListResponse,
  TLConversationRequest,
  TLConversationResponse,
  TLConversationSummary,
  TLConversationUpdateRequest,
  TLCreateShareRequest,
  TLCreateShareResponse,
  TLDeleteAllConversationsResponse,
  TLEditorChatMessageRequest,
  TLForkFromShareRequest,
  TLForkFromShareResponse,
  TLGrantPermissionRequest,
  TLGrantPermissionResponse,
  TLHTTPValidationError,
  TLPaginatedResponseProgram,
  TLPreviewResponse,
  TLPreviewSearchResponse,
  TLProgramsPermission,
  TLProgramsRevokePermissionsRequest,
  TLProgramsRevokePermissionsResponse,
  TLRenameConversationRequest,
  TLRenameConversationResponse,
  TLShareMetadataResponse,
  TLSkillExportResponse,
  TLSuccessResponse,
  TLSummarizeConversationResponse,
  TLToolExecutionDetailsResponse,
  TLToolExecutionsListResponse,
  TLWorkflowExportResponse,
  UpdateConversationPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Conversation
 */
export const conversation = (
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formUrlEncoded = new URLSearchParams();
  if (tLConversationRequest.attachments !== undefined) {
    tLConversationRequest.attachments.forEach((value) => formUrlEncoded.append(`attachments`, value));
  }
  if (tLConversationRequest.conversation_id !== undefined) {
    formUrlEncoded.append(`conversation_id`, tLConversationRequest.conversation_id);
  }
  formUrlEncoded.append(`message`, tLConversationRequest.message);
  if (tLConversationRequest.turn_index !== undefined) {
    formUrlEncoded.append(`turn_index`, tLConversationRequest.turn_index.toString());
  }

  return apiInstance<TLChatResponse>(
    {
      url: `/conversations`,
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      data: formUrlEncoded,
      signal,
    },
    options,
  );
};

export const getConversationQueryKey = (tLConversationRequest?: BodyType<TLConversationRequest>) => {
  return ['POST', 'conversations', tLConversationRequest] as const;
};

export const getConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getConversationQueryKey(tLConversationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof conversation>>> = ({ signal }) =>
    conversation(tLConversationRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof conversation>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ConversationQueryResult = NonNullable<Awaited<ReturnType<typeof conversation>>>;
export type ConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useConversation<
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConversationRequest: BodyType<TLConversationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof conversation>>,
          TError,
          Awaited<ReturnType<typeof conversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useConversation<
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof conversation>>,
          TError,
          Awaited<ReturnType<typeof conversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useConversation<
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Conversation
 */

export function useConversation<
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getConversationQueryOptions(tLConversationRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Conversation
 */
export const prefetchConversationQuery = async <
  TData = Awaited<ReturnType<typeof conversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof conversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getConversationQueryOptions(tLConversationRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Conversation
 */
export const invalidateConversation = async (
  queryClient: QueryClient,
  tLConversationRequest: BodyType<TLConversationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getConversationQueryKey(tLConversationRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete All User Conversations
 */
export const deleteAllUserConversations = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLDeleteAllConversationsResponse>(
    { url: `/conversations/delete-all`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteAllUserConversationsQueryKey = () => {
  return ['DELETE', 'conversations', 'delete-all'] as const;
};

export const getDeleteAllUserConversationsQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteAllUserConversationsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteAllUserConversations>>> = ({ signal }) =>
    deleteAllUserConversations(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof deleteAllUserConversations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type DeleteAllUserConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof deleteAllUserConversations>>>;
export type DeleteAllUserConversationsQueryError = ErrorType<unknown>;

export function useDeleteAllUserConversations<
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteAllUserConversations>>,
          TError,
          Awaited<ReturnType<typeof deleteAllUserConversations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteAllUserConversations<
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteAllUserConversations>>,
          TError,
          Awaited<ReturnType<typeof deleteAllUserConversations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteAllUserConversations<
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete All User Conversations
 */

export function useDeleteAllUserConversations<
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteAllUserConversationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete All User Conversations
 */
export const prefetchDeleteAllUserConversationsQuery = async <
  TData = Awaited<ReturnType<typeof deleteAllUserConversations>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteAllUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteAllUserConversationsQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete All User Conversations
 */
export const invalidateDeleteAllUserConversations = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteAllUserConversationsQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Delete Conversation
 */
export const deleteConversation = (
  { conversationId }: DeleteConversationPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/conversations/delete/${conversationId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteConversationQueryKey = ({ conversationId }: DeleteConversationPathParameters) => {
  return ['DELETE', 'conversations', 'delete', conversationId] as const;
};

export const getDeleteConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: DeleteConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteConversationQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteConversation>>> = ({ signal }) =>
    deleteConversation({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteConversationQueryResult = NonNullable<Awaited<ReturnType<typeof deleteConversation>>>;
export type DeleteConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteConversation<
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConversationPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConversation>>,
          TError,
          Awaited<ReturnType<typeof deleteConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConversation<
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteConversation>>,
          TError,
          Awaited<ReturnType<typeof deleteConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteConversation<
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Conversation
 */

export function useDeleteConversation<
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: DeleteConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteConversationQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Conversation
 */
export const prefetchDeleteConversationQuery = async <
  TData = Awaited<ReturnType<typeof deleteConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: DeleteConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteConversationQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Conversation
 */
export const invalidateDeleteConversation = async (
  queryClient: QueryClient,
  { conversationId }: DeleteConversationPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteConversationQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * Chat with AI about code, optionally updating the active file with stateful conversation
 * @summary Chat With Code
 */
export const chatWithCode = (
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLChatResponse>(
    {
      url: `/conversations/editor`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLEditorChatMessageRequest,
      signal,
    },
    options,
  );
};

export const getChatWithCodeQueryKey = (tLEditorChatMessageRequest?: BodyType<TLEditorChatMessageRequest>) => {
  return ['POST', 'conversations', 'editor', tLEditorChatMessageRequest] as const;
};

export const getChatWithCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getChatWithCodeQueryKey(tLEditorChatMessageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof chatWithCode>>> = ({ signal }) =>
    chatWithCode(tLEditorChatMessageRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof chatWithCode>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ChatWithCodeQueryResult = NonNullable<Awaited<ReturnType<typeof chatWithCode>>>;
export type ChatWithCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useChatWithCode<
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof chatWithCode>>,
          TError,
          Awaited<ReturnType<typeof chatWithCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useChatWithCode<
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof chatWithCode>>,
          TError,
          Awaited<ReturnType<typeof chatWithCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useChatWithCode<
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Chat With Code
 */

export function useChatWithCode<
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getChatWithCodeQueryOptions(tLEditorChatMessageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Chat With Code
 */
export const prefetchChatWithCodeQuery = async <
  TData = Awaited<ReturnType<typeof chatWithCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof chatWithCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getChatWithCodeQueryOptions(tLEditorChatMessageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Chat With Code
 */
export const invalidateChatWithCode = async (
  queryClient: QueryClient,
  tLEditorChatMessageRequest: BodyType<TLEditorChatMessageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getChatWithCodeQueryKey(tLEditorChatMessageRequest) }, options);

  return queryClient;
};

/**
 * Import a shared snapshot as a new conversation in the request's tenant
 * (X-Tenant-ID picks the fork target). The source conversation is never touched.
 * A token names no conversation, so the surface's own roles are checked service-side
 * against the snapshot's type.
 * @summary Fork Conversation From Share
 */
export const forkConversationFromShare = (
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLForkFromShareResponse>(
    {
      url: `/conversations/fork-from-share`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLForkFromShareRequest,
      signal,
    },
    options,
  );
};

export const getForkConversationFromShareQueryKey = (tLForkFromShareRequest?: BodyType<TLForkFromShareRequest>) => {
  return ['POST', 'conversations', 'fork-from-share', tLForkFromShareRequest] as const;
};

export const getForkConversationFromShareQueryOptions = <
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getForkConversationFromShareQueryKey(tLForkFromShareRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof forkConversationFromShare>>> = ({ signal }) =>
    forkConversationFromShare(tLForkFromShareRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof forkConversationFromShare>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ForkConversationFromShareQueryResult = NonNullable<Awaited<ReturnType<typeof forkConversationFromShare>>>;
export type ForkConversationFromShareQueryError = ErrorType<TLHTTPValidationError>;

export function useForkConversationFromShare<
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof forkConversationFromShare>>,
          TError,
          Awaited<ReturnType<typeof forkConversationFromShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useForkConversationFromShare<
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof forkConversationFromShare>>,
          TError,
          Awaited<ReturnType<typeof forkConversationFromShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useForkConversationFromShare<
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Fork Conversation From Share
 */

export function useForkConversationFromShare<
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getForkConversationFromShareQueryOptions(tLForkFromShareRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Fork Conversation From Share
 */
export const prefetchForkConversationFromShareQuery = async <
  TData = Awaited<ReturnType<typeof forkConversationFromShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversationFromShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getForkConversationFromShareQueryOptions(tLForkFromShareRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Fork Conversation From Share
 */
export const invalidateForkConversationFromShare = async (
  queryClient: QueryClient,
  tLForkFromShareRequest: BodyType<TLForkFromShareRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getForkConversationFromShareQueryKey(tLForkFromShareRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get User Conversations
 */
export const getUserConversations = (
  params?: GetUserConversationsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationListResponse>(
    { url: `/conversations/list`, method: 'GET', params, signal },
    options,
  );
};

export const getGetUserConversationsQueryKey = (params?: GetUserConversationsParams) => {
  return ['conversations', 'list', ...(params ? [params] : [])] as const;
};

export const getGetUserConversationsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserConversationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserConversationsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserConversations>>> = ({ signal }) =>
    getUserConversations(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserConversations>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserConversationsQueryResult = NonNullable<Awaited<ReturnType<typeof getUserConversations>>>;
export type GetUserConversationsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUserConversations<
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetUserConversationsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserConversations>>,
          TError,
          Awaited<ReturnType<typeof getUserConversations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserConversations<
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserConversationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserConversations>>,
          TError,
          Awaited<ReturnType<typeof getUserConversations>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserConversations<
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserConversationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Conversations
 */

export function useGetUserConversations<
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserConversationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserConversationsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get User Conversations
 */
export const prefetchGetUserConversationsQuery = async <
  TData = Awaited<ReturnType<typeof getUserConversations>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetUserConversationsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserConversations>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUserConversationsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get User Conversations
 */
export const invalidateGetUserConversations = async (
  queryClient: QueryClient,
  params?: GetUserConversationsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUserConversationsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Message Tool Executions
 */
export const getMessageToolExecutions = (
  { messageId }: GetMessageToolExecutionsPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLToolExecutionsListResponse>(
    { url: `/conversations/messages/${messageId}/tool-executions`, method: 'GET', signal },
    options,
  );
};

export const getGetMessageToolExecutionsQueryKey = ({ messageId }: GetMessageToolExecutionsPathParameters) => {
  return ['conversations', 'messages', messageId, 'tool-executions'] as const;
};

export const getGetMessageToolExecutionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { messageId }: GetMessageToolExecutionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMessageToolExecutionsQueryKey({ messageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMessageToolExecutions>>> = ({ signal }) =>
    getMessageToolExecutions({ messageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetMessageToolExecutionsQueryResult = NonNullable<Awaited<ReturnType<typeof getMessageToolExecutions>>>;
export type GetMessageToolExecutionsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetMessageToolExecutions<
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMessageToolExecutionsPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessageToolExecutions>>,
          TError,
          Awaited<ReturnType<typeof getMessageToolExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMessageToolExecutions<
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMessageToolExecutionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessageToolExecutions>>,
          TError,
          Awaited<ReturnType<typeof getMessageToolExecutions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMessageToolExecutions<
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMessageToolExecutionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Message Tool Executions
 */

export function useGetMessageToolExecutions<
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { messageId }: GetMessageToolExecutionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMessageToolExecutionsQueryOptions({ messageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Message Tool Executions
 */
export const prefetchGetMessageToolExecutionsQuery = async <
  TData = Awaited<ReturnType<typeof getMessageToolExecutions>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { messageId }: GetMessageToolExecutionsPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageToolExecutions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetMessageToolExecutionsQueryOptions({ messageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Message Tool Executions
 */
export const invalidateGetMessageToolExecutions = async (
  queryClient: QueryClient,
  { messageId }: GetMessageToolExecutionsPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetMessageToolExecutionsQueryKey({ messageId }) }, options);

  return queryClient;
};

/**
 * Revoke one or more users' access to a program in chat.
 * @summary Revoke Permissions
 */
export const revokePermissions = (
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgramsRevokePermissionsResponse | void>(
    {
      url: `/conversations/programs/permissions/revoke`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLProgramsRevokePermissionsRequest,
      signal,
    },
    options,
  );
};

export const getRevokePermissionsQueryKey = (
  tLProgramsRevokePermissionsRequest?: BodyType<TLProgramsRevokePermissionsRequest>,
) => {
  return ['POST', 'conversations', 'programs', 'permissions', 'revoke', tLProgramsRevokePermissionsRequest] as const;
};

export const getRevokePermissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof revokePermissions>>,
  TError = ErrorType<void>,
>(
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRevokePermissionsQueryKey(tLProgramsRevokePermissionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof revokePermissions>>> = ({ signal }) =>
    revokePermissions(tLProgramsRevokePermissionsRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof revokePermissions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RevokePermissionsQueryResult = NonNullable<Awaited<ReturnType<typeof revokePermissions>>>;
export type RevokePermissionsQueryError = ErrorType<void>;

export function useRevokePermissions<TData = Awaited<ReturnType<typeof revokePermissions>>, TError = ErrorType<void>>(
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokePermissions>>,
          TError,
          Awaited<ReturnType<typeof revokePermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokePermissions<TData = Awaited<ReturnType<typeof revokePermissions>>, TError = ErrorType<void>>(
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokePermissions>>,
          TError,
          Awaited<ReturnType<typeof revokePermissions>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokePermissions<TData = Awaited<ReturnType<typeof revokePermissions>>, TError = ErrorType<void>>(
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Revoke Permissions
 */

export function useRevokePermissions<TData = Awaited<ReturnType<typeof revokePermissions>>, TError = ErrorType<void>>(
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRevokePermissionsQueryOptions(tLProgramsRevokePermissionsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Revoke Permissions
 */
export const prefetchRevokePermissionsQuery = async <
  TData = Awaited<ReturnType<typeof revokePermissions>>,
  TError = ErrorType<void>,
>(
  queryClient: QueryClient,
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokePermissions>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRevokePermissionsQueryOptions(tLProgramsRevokePermissionsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Revoke Permissions
 */
export const invalidateRevokePermissions = async (
  queryClient: QueryClient,
  tLProgramsRevokePermissionsRequest: BodyType<TLProgramsRevokePermissionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRevokePermissionsQueryKey(tLProgramsRevokePermissionsRequest) },
    options,
  );

  return queryClient;
};

/**
 * Get all programs shared with the current user in chat.
 * @summary Get Shared Programs
 */
export const getSharedPrograms = (
  params?: GetSharedProgramsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseProgram>(
    { url: `/conversations/programs/shared`, method: 'GET', params, signal },
    options,
  );
};

export const getGetSharedProgramsQueryKey = (params?: GetSharedProgramsParams) => {
  return ['conversations', 'programs', 'shared', ...(params ? [params] : [])] as const;
};

export const getGetSharedProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSharedProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSharedProgramsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSharedPrograms>>> = ({ signal }) =>
    getSharedPrograms(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSharedPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSharedProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof getSharedPrograms>>>;
export type GetSharedProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetSharedPrograms<
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetSharedProgramsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSharedPrograms>>,
          TError,
          Awaited<ReturnType<typeof getSharedPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedPrograms<
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSharedProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSharedPrograms>>,
          TError,
          Awaited<ReturnType<typeof getSharedPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetSharedPrograms<
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSharedProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Shared Programs
 */

export function useGetSharedPrograms<
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetSharedProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetSharedProgramsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Shared Programs
 */
export const prefetchGetSharedProgramsQuery = async <
  TData = Awaited<ReturnType<typeof getSharedPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetSharedProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getSharedPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetSharedProgramsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Shared Programs
 */
export const invalidateGetSharedPrograms = async (
  queryClient: QueryClient,
  params?: GetSharedProgramsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetSharedProgramsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get User Programs
 */
export const getUserPrograms = (
  params?: GetUserProgramsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseProgram>(
    { url: `/conversations/programs/user`, method: 'GET', params, signal },
    options,
  );
};

export const getGetUserProgramsQueryKey = (params?: GetUserProgramsParams) => {
  return ['conversations', 'programs', 'user', ...(params ? [params] : [])] as const;
};

export const getGetUserProgramsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserProgramsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserPrograms>>> = ({ signal }) =>
    getUserPrograms(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserPrograms>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUserProgramsQueryResult = NonNullable<Awaited<ReturnType<typeof getUserPrograms>>>;
export type GetUserProgramsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetUserPrograms<
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetUserProgramsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserPrograms>>,
          TError,
          Awaited<ReturnType<typeof getUserPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserPrograms<
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserPrograms>>,
          TError,
          Awaited<ReturnType<typeof getUserPrograms>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetUserPrograms<
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get User Programs
 */

export function useGetUserPrograms<
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetUserProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetUserProgramsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get User Programs
 */
export const prefetchGetUserProgramsQuery = async <
  TData = Awaited<ReturnType<typeof getUserPrograms>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetUserProgramsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserPrograms>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetUserProgramsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get User Programs
 */
export const invalidateGetUserPrograms = async (
  queryClient: QueryClient,
  params?: GetUserProgramsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetUserProgramsQueryKey(params) }, options);

  return queryClient;
};

/**
 * Get all users who have access to a program in chat.
 * @summary Get Program Permissions By Program Id
 */
export const getProgramPermissionsByProgramId = (
  { programId }: GetProgramPermissionsByProgramIdPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLProgramsPermission[]>(
    { url: `/conversations/programs/${programId}/permissions`, method: 'GET', signal },
    options,
  );
};

export const getGetProgramPermissionsByProgramIdQueryKey = ({
  programId,
}: GetProgramPermissionsByProgramIdPathParameters) => {
  return ['conversations', 'programs', programId, 'permissions'] as const;
};

export const getGetProgramPermissionsByProgramIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPermissionsByProgramIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProgramPermissionsByProgramIdQueryKey({ programId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>> = ({ signal }) =>
    getProgramPermissionsByProgramId({ programId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProgramPermissionsByProgramIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>
>;
export type GetProgramPermissionsByProgramIdQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProgramPermissionsByProgramId<
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPermissionsByProgramIdPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
          TError,
          Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramPermissionsByProgramId<
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPermissionsByProgramIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
          TError,
          Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProgramPermissionsByProgramId<
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProgramPermissionsByProgramIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Program Permissions By Program Id
 */

export function useGetProgramPermissionsByProgramId<
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { programId }: GetProgramPermissionsByProgramIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProgramPermissionsByProgramIdQueryOptions({ programId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Program Permissions By Program Id
 */
export const prefetchGetProgramPermissionsByProgramIdQuery = async <
  TData = Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { programId }: GetProgramPermissionsByProgramIdPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProgramPermissionsByProgramId>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProgramPermissionsByProgramIdQueryOptions({ programId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Program Permissions By Program Id
 */
export const invalidateGetProgramPermissionsByProgramId = async (
  queryClient: QueryClient,
  { programId }: GetProgramPermissionsByProgramIdPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetProgramPermissionsByProgramIdQueryKey({ programId }) },
    options,
  );

  return queryClient;
};

/**
 * Grant access to a program for one or more users in chat.
 * @summary Grant Permission
 */
export const grantPermission = (
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLGrantPermissionResponse | void>(
    {
      url: `/conversations/programs/${programId}/permissions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGrantPermissionRequest,
      signal,
    },
    options,
  );
};

export const getGrantPermissionQueryKey = (
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest?: BodyType<TLGrantPermissionRequest>,
) => {
  return ['POST', 'conversations', 'programs', programId, 'permissions', tLGrantPermissionRequest] as const;
};

export const getGrantPermissionQueryOptions = <
  TData = Awaited<ReturnType<typeof grantPermission>>,
  TError = ErrorType<void>,
>(
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGrantPermissionQueryKey({ programId }, tLGrantPermissionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof grantPermission>>> = ({ signal }) =>
    grantPermission({ programId }, tLGrantPermissionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GrantPermissionQueryResult = NonNullable<Awaited<ReturnType<typeof grantPermission>>>;
export type GrantPermissionQueryError = ErrorType<void>;

export function useGrantPermission<TData = Awaited<ReturnType<typeof grantPermission>>, TError = ErrorType<void>>(
  pathParams: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantPermission>>,
          TError,
          Awaited<ReturnType<typeof grantPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantPermission<TData = Awaited<ReturnType<typeof grantPermission>>, TError = ErrorType<void>>(
  pathParams: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantPermission>>,
          TError,
          Awaited<ReturnType<typeof grantPermission>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantPermission<TData = Awaited<ReturnType<typeof grantPermission>>, TError = ErrorType<void>>(
  pathParams: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Grant Permission
 */

export function useGrantPermission<TData = Awaited<ReturnType<typeof grantPermission>>, TError = ErrorType<void>>(
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGrantPermissionQueryOptions({ programId }, tLGrantPermissionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Grant Permission
 */
export const prefetchGrantPermissionQuery = async <
  TData = Awaited<ReturnType<typeof grantPermission>>,
  TError = ErrorType<void>,
>(
  queryClient: QueryClient,
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantPermission>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGrantPermissionQueryOptions({ programId }, tLGrantPermissionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Grant Permission
 */
export const invalidateGrantPermission = async (
  queryClient: QueryClient,
  { programId }: GrantPermissionPathParameters,
  tLGrantPermissionRequest: BodyType<TLGrantPermissionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGrantPermissionQueryKey({ programId }, tLGrantPermissionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Rename Conversation
 */
export const renameConversation = (
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLRenameConversationResponse>(
    {
      url: `/conversations/rename/${conversationId}`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLRenameConversationRequest,
      signal,
    },
    options,
  );
};

export const getRenameConversationQueryKey = (
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest?: BodyType<TLRenameConversationRequest>,
) => {
  return ['POST', 'conversations', 'rename', conversationId, tLRenameConversationRequest] as const;
};

export const getRenameConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRenameConversationQueryKey({ conversationId }, tLRenameConversationRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof renameConversation>>> = ({ signal }) =>
    renameConversation({ conversationId }, tLRenameConversationRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RenameConversationQueryResult = NonNullable<Awaited<ReturnType<typeof renameConversation>>>;
export type RenameConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useRenameConversation<
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameConversation>>,
          TError,
          Awaited<ReturnType<typeof renameConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameConversation<
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof renameConversation>>,
          TError,
          Awaited<ReturnType<typeof renameConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRenameConversation<
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rename Conversation
 */

export function useRenameConversation<
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRenameConversationQueryOptions({ conversationId }, tLRenameConversationRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rename Conversation
 */
export const prefetchRenameConversationQuery = async <
  TData = Awaited<ReturnType<typeof renameConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof renameConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRenameConversationQueryOptions({ conversationId }, tLRenameConversationRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Rename Conversation
 */
export const invalidateRenameConversation = async (
  queryClient: QueryClient,
  { conversationId }: RenameConversationPathParameters,
  tLRenameConversationRequest: BodyType<TLRenameConversationRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRenameConversationQueryKey({ conversationId }, tLRenameConversationRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Tool Execution
 */
export const getToolExecution = (
  { toolExecutionId }: GetToolExecutionPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLToolExecutionDetailsResponse>(
    { url: `/conversations/tool-executions/${toolExecutionId}`, method: 'GET', signal },
    options,
  );
};

export const getGetToolExecutionQueryKey = ({ toolExecutionId }: GetToolExecutionPathParameters) => {
  return ['conversations', 'tool-executions', toolExecutionId] as const;
};

export const getGetToolExecutionQueryOptions = <
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { toolExecutionId }: GetToolExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetToolExecutionQueryKey({ toolExecutionId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getToolExecution>>> = ({ signal }) =>
    getToolExecution({ toolExecutionId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: toolExecutionId !== null && toolExecutionId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetToolExecutionQueryResult = NonNullable<Awaited<ReturnType<typeof getToolExecution>>>;
export type GetToolExecutionQueryError = ErrorType<TLHTTPValidationError>;

export function useGetToolExecution<
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetToolExecutionPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getToolExecution>>,
          TError,
          Awaited<ReturnType<typeof getToolExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetToolExecution<
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetToolExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getToolExecution>>,
          TError,
          Awaited<ReturnType<typeof getToolExecution>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetToolExecution<
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetToolExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Tool Execution
 */

export function useGetToolExecution<
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { toolExecutionId }: GetToolExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetToolExecutionQueryOptions({ toolExecutionId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Tool Execution
 */
export const prefetchGetToolExecutionQuery = async <
  TData = Awaited<ReturnType<typeof getToolExecution>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { toolExecutionId }: GetToolExecutionPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getToolExecution>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetToolExecutionQueryOptions({ toolExecutionId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Tool Execution
 */
export const invalidateGetToolExecution = async (
  queryClient: QueryClient,
  { toolExecutionId }: GetToolExecutionPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetToolExecutionQueryKey({ toolExecutionId }) }, options);

  return queryClient;
};

/**
 * @summary Get Conversation
 */
export const getConversation = (
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationResponse>(
    { url: `/conversations/${conversationId}`, method: 'GET', params, signal },
    options,
  );
};

export const getGetConversationQueryKey = (
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
) => {
  return ['conversations', conversationId, ...(params ? [params] : [])] as const;
};

export const getGetConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConversationQueryKey({ conversationId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConversation>>> = ({ signal }) =>
    getConversation({ conversationId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConversationQueryResult = NonNullable<Awaited<ReturnType<typeof getConversation>>>;
export type GetConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConversation<
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationPathParameters,
  params: undefined | GetConversationParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversation>>,
          TError,
          Awaited<ReturnType<typeof getConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversation<
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversation>>,
          TError,
          Awaited<ReturnType<typeof getConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversation<
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Conversation
 */

export function useGetConversation<
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConversationQueryOptions({ conversationId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Conversation
 */
export const prefetchGetConversationQuery = async <
  TData = Awaited<ReturnType<typeof getConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConversationQueryOptions({ conversationId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Conversation
 */
export const invalidateGetConversation = async (
  queryClient: QueryClient,
  { conversationId }: GetConversationPathParameters,
  params?: GetConversationParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConversationQueryKey({ conversationId }, params) }, options);

  return queryClient;
};

/**
 * @summary Update Conversation
 */
export const updateConversation = (
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationSummary>(
    {
      url: `/conversations/${conversationId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLConversationUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateConversationQueryKey = (
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest?: BodyType<TLConversationUpdateRequest>,
) => {
  return ['PATCH', 'conversations', conversationId, tLConversationUpdateRequest] as const;
};

export const getUpdateConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateConversationQueryKey({ conversationId }, tLConversationUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateConversation>>> = ({ signal }) =>
    updateConversation({ conversationId }, tLConversationUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateConversationQueryResult = NonNullable<Awaited<ReturnType<typeof updateConversation>>>;
export type UpdateConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateConversation<
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConversation>>,
          TError,
          Awaited<ReturnType<typeof updateConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConversation<
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateConversation>>,
          TError,
          Awaited<ReturnType<typeof updateConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateConversation<
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Conversation
 */

export function useUpdateConversation<
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateConversationQueryOptions({ conversationId }, tLConversationUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Conversation
 */
export const prefetchUpdateConversationQuery = async <
  TData = Awaited<ReturnType<typeof updateConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateConversationQueryOptions({ conversationId }, tLConversationUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Conversation
 */
export const invalidateUpdateConversation = async (
  queryClient: QueryClient,
  { conversationId }: UpdateConversationPathParameters,
  tLConversationUpdateRequest: BodyType<TLConversationUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateConversationQueryKey({ conversationId }, tLConversationUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Conversation To Skill
 */
export const exportConversationToSkill = (
  { conversationId }: ExportConversationToSkillPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSkillExportResponse>(
    { url: `/conversations/${conversationId}/export/skill`, method: 'POST', signal },
    options,
  );
};

export const getExportConversationToSkillQueryKey = ({ conversationId }: ExportConversationToSkillPathParameters) => {
  return ['POST', 'conversations', conversationId, 'export', 'skill'] as const;
};

export const getExportConversationToSkillQueryOptions = <
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExportConversationToSkillQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportConversationToSkill>>> = ({ signal }) =>
    exportConversationToSkill({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportConversationToSkillQueryResult = NonNullable<Awaited<ReturnType<typeof exportConversationToSkill>>>;
export type ExportConversationToSkillQueryError = ErrorType<TLHTTPValidationError>;

export function useExportConversationToSkill<
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToSkillPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportConversationToSkill>>,
          TError,
          Awaited<ReturnType<typeof exportConversationToSkill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportConversationToSkill<
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportConversationToSkill>>,
          TError,
          Awaited<ReturnType<typeof exportConversationToSkill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportConversationToSkill<
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Conversation To Skill
 */

export function useExportConversationToSkill<
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportConversationToSkillQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Conversation To Skill
 */
export const prefetchExportConversationToSkillQuery = async <
  TData = Awaited<ReturnType<typeof exportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: ExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportConversationToSkillQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Conversation To Skill
 */
export const invalidateExportConversationToSkill = async (
  queryClient: QueryClient,
  { conversationId }: ExportConversationToSkillPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getExportConversationToSkillQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * @summary Stream Export Conversation To Skill
 */
export const streamExportConversationToSkill = (
  { conversationId }: StreamExportConversationToSkillPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>(
    { url: `/conversations/${conversationId}/export/skill/stream`, method: 'POST', signal },
    options,
  );
};

export const getStreamExportConversationToSkillQueryKey = ({
  conversationId,
}: StreamExportConversationToSkillPathParameters) => {
  return ['POST', 'conversations', conversationId, 'export', 'skill', 'stream'] as const;
};

export const getStreamExportConversationToSkillQueryOptions = <
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: StreamExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStreamExportConversationToSkillQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof streamExportConversationToSkill>>> = ({ signal }) =>
    streamExportConversationToSkill({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type StreamExportConversationToSkillQueryResult = NonNullable<
  Awaited<ReturnType<typeof streamExportConversationToSkill>>
>;
export type StreamExportConversationToSkillQueryError = ErrorType<TLHTTPValidationError>;

export function useStreamExportConversationToSkill<
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToSkillPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamExportConversationToSkill>>,
          TError,
          Awaited<ReturnType<typeof streamExportConversationToSkill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamExportConversationToSkill<
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamExportConversationToSkill>>,
          TError,
          Awaited<ReturnType<typeof streamExportConversationToSkill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamExportConversationToSkill<
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Stream Export Conversation To Skill
 */

export function useStreamExportConversationToSkill<
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: StreamExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStreamExportConversationToSkillQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Stream Export Conversation To Skill
 */
export const prefetchStreamExportConversationToSkillQuery = async <
  TData = Awaited<ReturnType<typeof streamExportConversationToSkill>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: StreamExportConversationToSkillPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToSkill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStreamExportConversationToSkillQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Stream Export Conversation To Skill
 */
export const invalidateStreamExportConversationToSkill = async (
  queryClient: QueryClient,
  { conversationId }: StreamExportConversationToSkillPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getStreamExportConversationToSkillQueryKey({ conversationId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Export Conversation To Workflow
 */
export const exportConversationToWorkflow = (
  { conversationId }: ExportConversationToWorkflowPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowExportResponse>(
    { url: `/conversations/${conversationId}/export/workflow`, method: 'POST', signal },
    options,
  );
};

export const getExportConversationToWorkflowQueryKey = ({
  conversationId,
}: ExportConversationToWorkflowPathParameters) => {
  return ['POST', 'conversations', conversationId, 'export', 'workflow'] as const;
};

export const getExportConversationToWorkflowQueryOptions = <
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getExportConversationToWorkflowQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof exportConversationToWorkflow>>> = ({ signal }) =>
    exportConversationToWorkflow({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ExportConversationToWorkflowQueryResult = NonNullable<
  Awaited<ReturnType<typeof exportConversationToWorkflow>>
>;
export type ExportConversationToWorkflowQueryError = ErrorType<TLHTTPValidationError>;

export function useExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToWorkflowPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportConversationToWorkflow>>,
          TError,
          Awaited<ReturnType<typeof exportConversationToWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportConversationToWorkflow>>,
          TError,
          Awaited<ReturnType<typeof exportConversationToWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Export Conversation To Workflow
 */

export function useExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getExportConversationToWorkflowQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Export Conversation To Workflow
 */
export const prefetchExportConversationToWorkflowQuery = async <
  TData = Awaited<ReturnType<typeof exportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: ExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof exportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getExportConversationToWorkflowQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Export Conversation To Workflow
 */
export const invalidateExportConversationToWorkflow = async (
  queryClient: QueryClient,
  { conversationId }: ExportConversationToWorkflowPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getExportConversationToWorkflowQueryKey({ conversationId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Stream Export Conversation To Workflow
 */
export const streamExportConversationToWorkflow = (
  { conversationId }: StreamExportConversationToWorkflowPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>(
    { url: `/conversations/${conversationId}/export/workflow/stream`, method: 'POST', signal },
    options,
  );
};

export const getStreamExportConversationToWorkflowQueryKey = ({
  conversationId,
}: StreamExportConversationToWorkflowPathParameters) => {
  return ['POST', 'conversations', conversationId, 'export', 'workflow', 'stream'] as const;
};

export const getStreamExportConversationToWorkflowQueryOptions = <
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: StreamExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStreamExportConversationToWorkflowQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>> = ({ signal }) =>
    streamExportConversationToWorkflow({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type StreamExportConversationToWorkflowQueryResult = NonNullable<
  Awaited<ReturnType<typeof streamExportConversationToWorkflow>>
>;
export type StreamExportConversationToWorkflowQueryError = ErrorType<TLHTTPValidationError>;

export function useStreamExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToWorkflowPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
          TError,
          Awaited<ReturnType<typeof streamExportConversationToWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
          TError,
          Awaited<ReturnType<typeof streamExportConversationToWorkflow>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Stream Export Conversation To Workflow
 */

export function useStreamExportConversationToWorkflow<
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: StreamExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStreamExportConversationToWorkflowQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Stream Export Conversation To Workflow
 */
export const prefetchStreamExportConversationToWorkflowQuery = async <
  TData = Awaited<ReturnType<typeof streamExportConversationToWorkflow>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: StreamExportConversationToWorkflowPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamExportConversationToWorkflow>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStreamExportConversationToWorkflowQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Stream Export Conversation To Workflow
 */
export const invalidateStreamExportConversationToWorkflow = async (
  queryClient: QueryClient,
  { conversationId }: StreamExportConversationToWorkflowPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getStreamExportConversationToWorkflowQueryKey({ conversationId }) },
    options,
  );

  return queryClient;
};

/**
 * Get all files attached to a conversation with download URLs.
 * @summary Get Conversation Files
 */
export const getConversationFiles = (
  { conversationId }: GetConversationFilesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationFile[]>(
    { url: `/conversations/${conversationId}/files`, method: 'GET', signal },
    options,
  );
};

export const getGetConversationFilesQueryKey = ({ conversationId }: GetConversationFilesPathParameters) => {
  return ['conversations', conversationId, 'files'] as const;
};

export const getGetConversationFilesQueryOptions = <
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationFilesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConversationFilesQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConversationFiles>>> = ({ signal }) =>
    getConversationFiles({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConversationFilesQueryResult = NonNullable<Awaited<ReturnType<typeof getConversationFiles>>>;
export type GetConversationFilesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConversationFiles<
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationFilesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationFiles>>,
          TError,
          Awaited<ReturnType<typeof getConversationFiles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationFiles<
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationFilesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationFiles>>,
          TError,
          Awaited<ReturnType<typeof getConversationFiles>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationFiles<
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationFilesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Conversation Files
 */

export function useGetConversationFiles<
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationFilesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConversationFilesQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Conversation Files
 */
export const prefetchGetConversationFilesQuery = async <
  TData = Awaited<ReturnType<typeof getConversationFiles>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetConversationFilesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationFiles>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConversationFilesQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Conversation Files
 */
export const invalidateGetConversationFiles = async (
  queryClient: QueryClient,
  { conversationId }: GetConversationFilesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConversationFilesQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * Get a preview of a file attached to a conversation message.
 * @summary Get File Preview
 */
export const getFilePreview = (
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPreviewResponse>(
    { url: `/conversations/${conversationId}/files/${programId}/preview`, method: 'GET', params, signal },
    options,
  );
};

export const getGetFilePreviewQueryKey = (
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
) => {
  return ['conversations', conversationId, 'files', programId, 'preview', ...(params ? [params] : [])] as const;
};

export const getGetFilePreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFilePreviewQueryKey({ conversationId, programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFilePreview>>> = ({ signal }) =>
    getFilePreview({ conversationId, programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFilePreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getFilePreview>>>;
export type GetFilePreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFilePreview<
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilePreviewPathParameters,
  params: undefined | GetFilePreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFilePreview>>,
          TError,
          Awaited<ReturnType<typeof getFilePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFilePreview<
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFilePreview>>,
          TError,
          Awaited<ReturnType<typeof getFilePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFilePreview<
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get File Preview
 */

export function useGetFilePreview<
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFilePreviewQueryOptions({ conversationId, programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get File Preview
 */
export const prefetchGetFilePreviewQuery = async <
  TData = Awaited<ReturnType<typeof getFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFilePreviewQueryOptions({ conversationId, programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get File Preview
 */
export const invalidateGetFilePreview = async (
  queryClient: QueryClient,
  { conversationId, programId }: GetFilePreviewPathParameters,
  params?: GetFilePreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGetFilePreviewQueryKey({ conversationId, programId }, params) },
    options,
  );

  return queryClient;
};

/**
 * Search a text/code preview of a file attached to a conversation message.
 * @summary Search Conversation File Preview
 */
export const searchConversationFilePreview = (
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPreviewSearchResponse>(
    { url: `/conversations/${conversationId}/files/${programId}/preview/search`, method: 'GET', params, signal },
    options,
  );
};

export const getSearchConversationFilePreviewQueryKey = (
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params?: SearchConversationFilePreviewParams,
) => {
  return [
    'conversations',
    conversationId,
    'files',
    programId,
    'preview',
    'search',
    ...(params ? [params] : []),
  ] as const;
};

export const getSearchConversationFilePreviewQueryOptions = <
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSearchConversationFilePreviewQueryKey({ conversationId, programId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchConversationFilePreview>>> = ({ signal }) =>
    searchConversationFilePreview({ conversationId, programId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && programId !== null && programId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SearchConversationFilePreviewQueryResult = NonNullable<
  Awaited<ReturnType<typeof searchConversationFilePreview>>
>;
export type SearchConversationFilePreviewQueryError = ErrorType<TLHTTPValidationError>;

export function useSearchConversationFilePreview<
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchConversationFilePreview>>,
          TError,
          Awaited<ReturnType<typeof searchConversationFilePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchConversationFilePreview<
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchConversationFilePreview>>,
          TError,
          Awaited<ReturnType<typeof searchConversationFilePreview>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchConversationFilePreview<
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Search Conversation File Preview
 */

export function useSearchConversationFilePreview<
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchConversationFilePreviewQueryOptions({ conversationId, programId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Search Conversation File Preview
 */
export const prefetchSearchConversationFilePreviewQuery = async <
  TData = Awaited<ReturnType<typeof searchConversationFilePreview>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchConversationFilePreview>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSearchConversationFilePreviewQueryOptions({ conversationId, programId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Search Conversation File Preview
 */
export const invalidateSearchConversationFilePreview = async (
  queryClient: QueryClient,
  { conversationId, programId }: SearchConversationFilePreviewPathParameters,
  params: SearchConversationFilePreviewParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSearchConversationFilePreviewQueryKey({ conversationId, programId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Fork Conversation
 */
export const forkConversation = (
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLForkFromShareResponse>(
    {
      url: `/conversations/${conversationId}/fork`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLConversationForkRequest,
      signal,
    },
    options,
  );
};

export const getForkConversationQueryKey = (
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest?: BodyType<TLConversationForkRequest>,
) => {
  return ['POST', 'conversations', conversationId, 'fork', tLConversationForkRequest] as const;
};

export const getForkConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getForkConversationQueryKey({ conversationId }, tLConversationForkRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof forkConversation>>> = ({ signal }) =>
    forkConversation({ conversationId }, tLConversationForkRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ForkConversationQueryResult = NonNullable<Awaited<ReturnType<typeof forkConversation>>>;
export type ForkConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useForkConversation<
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof forkConversation>>,
          TError,
          Awaited<ReturnType<typeof forkConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useForkConversation<
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof forkConversation>>,
          TError,
          Awaited<ReturnType<typeof forkConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useForkConversation<
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Fork Conversation
 */

export function useForkConversation<
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getForkConversationQueryOptions({ conversationId }, tLConversationForkRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Fork Conversation
 */
export const prefetchForkConversationQuery = async <
  TData = Awaited<ReturnType<typeof forkConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof forkConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getForkConversationQueryOptions({ conversationId }, tLConversationForkRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Fork Conversation
 */
export const invalidateForkConversation = async (
  queryClient: QueryClient,
  { conversationId }: ForkConversationPathParameters,
  tLConversationForkRequest: BodyType<TLConversationForkRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getForkConversationQueryKey({ conversationId }, tLConversationForkRequest) },
    options,
  );

  return queryClient;
};

/**
 * Metadata of the active share link for this conversation (owner only).
 * @summary Get Conversation Share
 */
export const getConversationShare = (
  { conversationId }: GetConversationSharePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLShareMetadataResponse>(
    { url: `/conversations/${conversationId}/share`, method: 'GET', signal },
    options,
  );
};

export const getGetConversationShareQueryKey = ({ conversationId }: GetConversationSharePathParameters) => {
  return ['conversations', conversationId, 'share'] as const;
};

export const getGetConversationShareQueryOptions = <
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConversationShareQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConversationShare>>> = ({ signal }) =>
    getConversationShare({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConversationShareQueryResult = NonNullable<Awaited<ReturnType<typeof getConversationShare>>>;
export type GetConversationShareQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConversationShare<
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationSharePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationShare>>,
          TError,
          Awaited<ReturnType<typeof getConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationShare<
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationShare>>,
          TError,
          Awaited<ReturnType<typeof getConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationShare<
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Conversation Share
 */

export function useGetConversationShare<
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConversationShareQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Conversation Share
 */
export const prefetchGetConversationShareQuery = async <
  TData = Awaited<ReturnType<typeof getConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConversationShareQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Conversation Share
 */
export const invalidateGetConversationShare = async (
  queryClient: QueryClient,
  { conversationId }: GetConversationSharePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConversationShareQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * Freeze a snapshot of the conversation and mint a share token (owner only).
 * Re-sharing revokes the previous link. The token is returned exactly once.
 * Surface-bound types (workflow, view-remediation, editor, remediation) are view-only:
 * allow_fork comes back false however it was requested.
 * @summary Create Conversation Share
 */
export const createConversationShare = (
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCreateShareResponse>(
    {
      url: `/conversations/${conversationId}/share`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateShareRequest,
      signal,
    },
    options,
  );
};

export const getCreateConversationShareQueryKey = (
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest?: BodyType<TLCreateShareRequest>,
) => {
  return ['POST', 'conversations', conversationId, 'share', tLCreateShareRequest] as const;
};

export const getCreateConversationShareQueryOptions = <
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateConversationShareQueryKey({ conversationId }, tLCreateShareRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createConversationShare>>> = ({ signal }) =>
    createConversationShare({ conversationId }, tLCreateShareRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateConversationShareQueryResult = NonNullable<Awaited<ReturnType<typeof createConversationShare>>>;
export type CreateConversationShareQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateConversationShare<
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConversationShare>>,
          TError,
          Awaited<ReturnType<typeof createConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConversationShare<
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createConversationShare>>,
          TError,
          Awaited<ReturnType<typeof createConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateConversationShare<
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Conversation Share
 */

export function useCreateConversationShare<
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateConversationShareQueryOptions({ conversationId }, tLCreateShareRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Conversation Share
 */
export const prefetchCreateConversationShareQuery = async <
  TData = Awaited<ReturnType<typeof createConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateConversationShareQueryOptions({ conversationId }, tLCreateShareRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Conversation Share
 */
export const invalidateCreateConversationShare = async (
  queryClient: QueryClient,
  { conversationId }: CreateConversationSharePathParameters,
  tLCreateShareRequest: BodyType<TLCreateShareRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateConversationShareQueryKey({ conversationId }, tLCreateShareRequest) },
    options,
  );

  return queryClient;
};

/**
 * Revoke the active share link for this conversation (owner only).
 * @summary Revoke Conversation Share
 */
export const revokeConversationShare = (
  { conversationId }: RevokeConversationSharePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/conversations/${conversationId}/share/revoke`, method: 'POST', signal },
    options,
  );
};

export const getRevokeConversationShareQueryKey = ({ conversationId }: RevokeConversationSharePathParameters) => {
  return ['POST', 'conversations', conversationId, 'share', 'revoke'] as const;
};

export const getRevokeConversationShareQueryOptions = <
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: RevokeConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRevokeConversationShareQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof revokeConversationShare>>> = ({ signal }) =>
    revokeConversationShare({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RevokeConversationShareQueryResult = NonNullable<Awaited<ReturnType<typeof revokeConversationShare>>>;
export type RevokeConversationShareQueryError = ErrorType<TLHTTPValidationError>;

export function useRevokeConversationShare<
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeConversationSharePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeConversationShare>>,
          TError,
          Awaited<ReturnType<typeof revokeConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeConversationShare<
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeConversationShare>>,
          TError,
          Awaited<ReturnType<typeof revokeConversationShare>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeConversationShare<
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Revoke Conversation Share
 */

export function useRevokeConversationShare<
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: RevokeConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRevokeConversationShareQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Revoke Conversation Share
 */
export const prefetchRevokeConversationShareQuery = async <
  TData = Awaited<ReturnType<typeof revokeConversationShare>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: RevokeConversationSharePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeConversationShare>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRevokeConversationShareQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Revoke Conversation Share
 */
export const invalidateRevokeConversationShare = async (
  queryClient: QueryClient,
  { conversationId }: RevokeConversationSharePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRevokeConversationShareQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * @summary Summarize Conversation
 */
export const summarizeConversation = (
  { conversationId }: SummarizeConversationPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSummarizeConversationResponse>(
    { url: `/conversations/${conversationId}/summarize`, method: 'POST', signal },
    options,
  );
};

export const getSummarizeConversationQueryKey = ({ conversationId }: SummarizeConversationPathParameters) => {
  return ['POST', 'conversations', conversationId, 'summarize'] as const;
};

export const getSummarizeConversationQueryOptions = <
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: SummarizeConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSummarizeConversationQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof summarizeConversation>>> = ({ signal }) =>
    summarizeConversation({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type SummarizeConversationQueryResult = NonNullable<Awaited<ReturnType<typeof summarizeConversation>>>;
export type SummarizeConversationQueryError = ErrorType<TLHTTPValidationError>;

export function useSummarizeConversation<
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SummarizeConversationPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof summarizeConversation>>,
          TError,
          Awaited<ReturnType<typeof summarizeConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSummarizeConversation<
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SummarizeConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof summarizeConversation>>,
          TError,
          Awaited<ReturnType<typeof summarizeConversation>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSummarizeConversation<
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SummarizeConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Summarize Conversation
 */

export function useSummarizeConversation<
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: SummarizeConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSummarizeConversationQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Summarize Conversation
 */
export const prefetchSummarizeConversationQuery = async <
  TData = Awaited<ReturnType<typeof summarizeConversation>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: SummarizeConversationPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof summarizeConversation>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSummarizeConversationQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Summarize Conversation
 */
export const invalidateSummarizeConversation = async (
  queryClient: QueryClient,
  { conversationId }: SummarizeConversationPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSummarizeConversationQueryKey({ conversationId }) }, options);

  return queryClient;
};
